import type {
	Product,
	ProductItem
} from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import type {
	BasketItem,
	Basket
} from "@nwt/norce-pwa/dist/runtime/types/api/shopping/basket";
import type { FilteredProductItem } from "@nwt/norce-pwa/dist/runtime/types/api/searchAndFilter/filters";
import type { CombinedItem } from "~/types/components/category";
import { createGtmItem } from "~/types/composables/useGtmEventsTypes";

export const useGtmEvents = () => {
	const gtm = useGtm();
	const norceStore = useNorceStore();
	const currencyCode = norceStore?.currentCurrency?.Code || "SEK";

	if (!gtm) {
		return {
			productImpression: () => {},
			productClick: () => {},
			productDetail: () => {},
			addToCart: () => {},
			addPaymentInfo: () => {},
			addShippingInfo: () => {},
			purchase: () => {},
			removeFromCart: () => {},
			beginCheckout: () => {},
			viewCart: () => {},
			addToWishlist: () => {}
		};
	}

	return {
		productImpression(
			products: Partial<FilteredProductItem>[] | CombinedItem[],
			categoryName?: string
		) {
			const productItems = products
				.map((item) => {
					if (
						"type" in item &&
						item.type === "product" &&
						"data" in item &&
						item.data
					) {
						return item.data as Partial<FilteredProductItem>;
					}
					return item as Partial<FilteredProductItem>;
				})
				.filter((item) => item != null);

			gtm.push({
				event: "view_item_list",
				ecommerce: {
					items: productItems
						.map((product) =>
							createGtmItem(product, currencyCode, categoryName)
						)
						.filter((item) => item != null)
				}
			});
		},

		productClick(product: Partial<FilteredProductItem> | ProductItem) {
			gtm.push({
				event: "select_item",
				ecommerce: {
					items: [createGtmItem(product, currencyCode)]
				}
			});
		},

		productDetail(product: Product) {
			gtm.push({
				event: "view_item",
				ecommerce: {
					items: [createGtmItem(product, currencyCode)]
				}
			});
		},

		beginCheckout(basket: Basket) {
			if (!basket?.Items?.length || !basket?.Summary) {
				console.warn("Basket items or summary are missing for purchase event");
				return;
			}

			gtm.push({
				event: "begin_checkout",
				ecommerce: {
					coupon: basket.DiscountCode || "",
					value: basket.Summary.Total.AmountIncVat,
					currency: basket.CurrencyCode || currencyCode,
					items: basket.Items?.map((item) => createGtmItem(item, currencyCode))
				}
			});
		},

		addPaymentInfo(basket: Basket, paymentMethod?: string) {
			gtm.push({
				event: "add_payment_info",
				ecommerce: {
					value: basket.Summary?.Total.AmountIncVat,
					currency: currencyCode,
					payment_type: paymentMethod || basket.PaymentMethodId?.toString(),
					items: basket.Items?.map((item) => createGtmItem(item, currencyCode))
				}
			});
		},

		addShippingInfo(basket: Basket, shippingMethod?: string) {
			gtm.push({
				event: "add_shipping_info",
				ecommerce: {
					value: basket.Summary?.Total.AmountIncVat,
					currency: currencyCode,
					shipping_tier: shippingMethod || basket.DeliveryMethodId?.toString(),
					items: basket.Items?.map((item) => createGtmItem(item, currencyCode))
				}
			});
		},

		purchase(basket: Basket) {
			if (!basket.Items || !basket.Summary) {
				console.warn("Basket items or summary are missing for purchase event");
				return;
			}

			gtm.push({
				event: "purchase",
				ecommerce: {
					transaction_id: basket.OrderReference,
					value: basket.Summary.Total.AmountIncVat,
					currency: basket.CurrencyCode || currencyCode,
					tax: basket.Summary.Total.Vat,
					shipping: basket.Summary.Freigt.AmountIncVat,
					coupon: basket.DiscountCode || "",
					items: basket.Items?.map((item) => createGtmItem(item, currencyCode))
				}
			});
		},

		addToCart(item: ProductItem | Product) {
			gtm.push({
				event: "add_to_cart",
				ecommerce: {
					items: [createGtmItem(item, currencyCode)]
				}
			});
		},

		removeFromCart(item: Partial<BasketItem>) {
			gtm.push({
				event: "remove_from_cart",
				ecommerce: {
					items: [createGtmItem(item, currencyCode)]
				}
			});
		},

		viewCart(items: BasketItem[], orderTotal: number) {
			gtm.push({
				event: "view_cart",
				ecommerce: {
					currency: currencyCode,
					value: orderTotal,
					items: items.map((item) => createGtmItem(item, currencyCode))
				}
			});
		},

		addToWishlist(item: Product | Partial<FilteredProductItem> | ProductItem) {
			gtm.push({
				event: "add_to_wishlist",
				ecommerce: {
					items: [createGtmItem(item, currencyCode)]
				}
			});
		},

		shippingMethodAdded(shippingMethod: object) {
			gtm.push({
				event: "shipping_method_added",
				ecommerce: {
					price: shippingMethod?.Price || 0,
					name: shippingMethod?.Name || "",
					carrier: shippingMethod?.Carrier || ""
				}
			});
		}
	};
};
